import { makeActionFactory } from './actionFactory';
import { LayoutSize, StreamId, CellIndex, MinutesPerSegment, UtcTime, SeekType, DurableCellState } from 'types';

export type ActionType =
    'SET_LAYOUT'
    | 'RESTORE_LAYOUT'
    | 'FILTER_DEVICES'
    | 'SET_DATE'
    | 'ASSIGN_STREAM'
    | 'ACTIVATE_CELL'
    | 'VIDEO_CONNECT_REQUEST'
    | 'VIDEO_CONNECT_SUCCESS'
    | 'VIDEO_CONNECT_FAILURE'
    | 'VIDEO_PLAY'
    | 'VIDEO_PAUSE'
    | 'VIDEO_MUTE'
    | 'VIDEO_UNMUTE'
    | 'SEEK'
    | 'FRAME_REQUEST'
    | 'FRAME_SUCCESS'
    | 'FRAME_FAILURE'
    | 'VIDEO_DISCONNECTED'
    | 'SET_ZOOM'
    | 'SET_SCROLL'
    | 'SET_SYNC'
    | 'SET_TIMELINE_LABEL_WIDTH'
    | 'SET_STREAM_POSITION'
    | 'SET_VIDEO_STATUS'
    | 'DISPLAY_VIDEO_ERROR'
    | 'DISMISS_VIDEO_ERROR'
;

const actionFactory = makeActionFactory<ActionType>();


//
//  SET_LAYOUT action instructs the app to switch to the specified layout
//
export interface SetLayoutAction {
    type: 'SET_LAYOUT';
    payload: LayoutSize;
}
export const setLayout = actionFactory.payload<SetLayoutAction, LayoutSize>('SET_LAYOUT');


//
//  RESTORE_LAYOUT action instructs the app to switch to the specified layout
//
export interface RestoreLayoutAction {
    type: 'RESTORE_LAYOUT';
    payload: {
        durableCells: ReadonlyArray<DurableCellState>;
        activeCellIndex: CellIndex;
    };
}
export const restoreLayout = actionFactory.payload<RestoreLayoutAction, RestoreLayoutAction['payload']>('RESTORE_LAYOUT');


//
//  FILTER_DEVICES action notifies the app that the device filter has changed
//
export interface FilterDevicesAction {
    type: 'FILTER_DEVICES';
    payload: string;
}
export const filterDevices = actionFactory.payload<FilterDevicesAction, string>('FILTER_DEVICES');


//
//  SET_DATE action instructs the app to switch to the specified date
//
export interface SetDateAction {
    type: 'SET_DATE';
    payload: {
        date: Date;
        preserveTimeOfDay: boolean;
    };
}
export const setDate = (payload: SetDateAction['payload']) => {
    if (isNaN(payload.date.getTime())) {
        throw new Error('Invalid date');
    }
    return actionFactory.payload<SetDateAction, SetDateAction['payload']>('SET_DATE')(payload);
};


//
//  ASSIGN_STREAM action puts the specified stream in the specified cell
//
export interface AssignStreamAction {
    type: 'ASSIGN_STREAM';
    payload: {
        streamId: StreamId;
        cellIndex: CellIndex;
        paused?: boolean;
    };
}
export const assignStream = actionFactory.payload<AssignStreamAction, AssignStreamAction['payload']>('ASSIGN_STREAM');


//
//  ACTIVATE_CELL action makes the specified cell active
//
export interface ActivateCellAction {
    type: 'ACTIVATE_CELL';
    payload: CellIndex;
}
export const activateCell = actionFactory.payload<ActivateCellAction, CellIndex>('ACTIVATE_CELL');


//
//  VIDEO_CONNECT_REQUEST action notifies the app that we're trying to load video in a specific cell
//
export interface VideoConnectRequestAction {
    type: 'VIDEO_CONNECT_REQUEST';
    payload: CellIndex;
}
export const videoConnectRequest = actionFactory.payload<VideoConnectRequestAction, CellIndex>('VIDEO_CONNECT_REQUEST');


//
//  VIDEO_CONNECT_SUCCESS action notifies the app that we successfully loaded video in a specific cell
//
export interface VideoConnectSuccessAction {
    type: 'VIDEO_CONNECT_SUCCESS';
    meta: {
        cellIndex: CellIndex;
        utcTime: UtcTime;
        live: boolean;
    };
}
export const videoConnectSuccess = actionFactory.meta<VideoConnectSuccessAction, VideoConnectSuccessAction['meta']>('VIDEO_CONNECT_SUCCESS');


//
//  VIDEO_CONNECT_FAILURE action notifies the app that we failed to load video in a specific cell
//
export interface VideoConnectFailureAction {
    type: 'VIDEO_CONNECT_FAILURE';
    payload: Error;
    error: true;
    meta: {
        cellIndex: CellIndex;
    };
}
export const videoConnectFailure = actionFactory.errorMeta<VideoConnectFailureAction, { cellIndex: CellIndex }>('VIDEO_CONNECT_FAILURE');


//
//  VIDEO_PLAY action notifies the app that video should start playing in a given cell
//
export interface VideoPlayAction {
    type: 'VIDEO_PLAY';
    payload: {
        cellIndex: CellIndex;
        live: boolean;
    };
}
export const videoPlay = actionFactory.payload<VideoPlayAction, { cellIndex: CellIndex; live: boolean }>('VIDEO_PLAY');


//
//  VIDEO_PAUSE action notifies the app that video should pause in a given cell
//
export interface VideoPauseAction {
    type: 'VIDEO_PAUSE';
    payload: CellIndex;
}
export const videoPause = actionFactory.payload<VideoPauseAction, CellIndex>('VIDEO_PAUSE');

//
//  VIDEO_MUTE action notifies the app that audio should stop in a given cell
//
export interface VideoMuteAction {
    type: 'VIDEO_MUTE';
    payload: CellIndex;
}
export const videoMute = actionFactory.payload<VideoMuteAction, CellIndex>('VIDEO_MUTE');

//
//  VIDEO_UNMUTE action notifies the app that audio should play in a given cell
//
export interface VideoUnmuteAction {
    type: 'VIDEO_UNMUTE';
    payload: CellIndex;
}
export const videoUnmute = actionFactory.payload<VideoUnmuteAction, CellIndex>('VIDEO_UNMUTE');

//
//  SEEK action notifies the app that we should seek to the requested time in a given cell
//
export interface SeekAction {
    type: 'SEEK';
    payload: {
        cellIndex: CellIndex;
        utcTime?: UtcTime;
        seekType?: SeekType;
    };
}
export const seek = actionFactory.payload<SeekAction, SeekAction['payload']>('SEEK');


//
//  FRAME_REQUEST action notifies the app that it is attemping to download a frame for the given cell
//
export interface FrameRequestAction {
    type: 'FRAME_REQUEST';
    payload: CellIndex;
}
export const frameRequest = actionFactory.payload<FrameRequestAction, CellIndex>('FRAME_REQUEST');


//
//  FRAME_SUCCESS action notifies the app that a seek request for the given cell was successful
//
export interface FrameSuccessAction {
    type: 'FRAME_SUCCESS';
    payload: string;
    meta: {
        cellIndex: CellIndex;
        utcTime: UtcTime;
    };
}
export const frameSuccess = actionFactory.payloadMeta<FrameSuccessAction, string, FrameSuccessAction['meta']>('FRAME_SUCCESS');


//
//  FRAME_FAILURE action notifies the app that a seek request for the given cell failed
//
export interface FrameFailureAction {
    type: 'FRAME_FAILURE';
    payload: Error;
    meta: {
        cellIndex: CellIndex;
        utcTime: UtcTime;
    };
    error: boolean;
}
export const frameFailure = actionFactory.errorMeta<FrameFailureAction, FrameFailureAction['meta']>('FRAME_FAILURE');


//
//  VIDEO_DISCONNECTED action notifies the app that video connection is terminated
//
export interface VideoDisconnectedAction {
    type: 'VIDEO_DISCONNECTED';
    payload: {
        cellIndex: CellIndex;
        utcTime: UtcTime;
    };
}
export const videoDisconnected = actionFactory.payload<VideoDisconnectedAction, { cellIndex: CellIndex; utcTime: UtcTime }>('VIDEO_DISCONNECTED');


//
//  SET_ZOOM action notifies the app to change the zoom level for a timeline (and any synced timelines)
//
export interface SetZoomAction {
    type: 'SET_ZOOM';
    payload: {
        minutesPerSegment: MinutesPerSegment;
        cellIndex: CellIndex;
    };
}
export const setZoom = actionFactory.payload<SetZoomAction, { minutesPerSegment: MinutesPerSegment; cellIndex: CellIndex }>('SET_ZOOM');


//
//  SET_SCROLL action notifies the app to change the scroll position for a timeline (and any synced timelines)
//
export interface SetScrollAction {
    type: 'SET_SCROLL';
    payload: {
        scroll: number; // [ 0 .. 1 ], where 0 is bottom; 1 is top
        cellIndex: CellIndex;
    };
}
export const setScroll = actionFactory.payload<SetScrollAction, { scroll: number; cellIndex: CellIndex }>('SET_SCROLL');


//
//  SET_SYNC action notifies the app to lock/unlock the zoom synchronization for a given timeline
//
export interface SetSyncAction {
    type: 'SET_SYNC';
    payload: {
        cellIndex: CellIndex;
        sync: boolean;
    };
}
export const setSync = actionFactory.payload<SetSyncAction, { cellIndex: CellIndex; sync: boolean }>('SET_SYNC');


//
//  SET_TIMELINE_LABEL_WIDTH action tells the app how much room to leave for labels on the timelines.
//  It is calculated once as an initialization step.
//
export interface SetTimelineLabelWidthAction {
    type: 'SET_TIMELINE_LABEL_WIDTH';
    payload: number;
}
export const setTimelineLabelWidth = actionFactory.payload<SetTimelineLabelWidthAction, number>('SET_TIMELINE_LABEL_WIDTH');


//
//  SET_STREAM_POSITION action tells the app to move the position indicator for a given cell.
//  Note: it does not change the video at all... only the representation of the current position on the timeline.
//  It is fired periodically by the timeKeeperSaga and also in response to certain video actions (e.g. starting, pausing, stopping, etc).
//
export interface SetStreamPositionAction {
    type: 'SET_STREAM_POSITION';
    payload: UtcTime;
    meta: {
        cellIndex: CellIndex;
    };
}
export const setStreamPosition = actionFactory.payloadMeta<SetStreamPositionAction, UtcTime, { cellIndex: CellIndex }>('SET_STREAM_POSITION');


//
//  DISPLAY_VIDEO_ERROR action tells the app to show an error for the given video cell
//
export interface DisplayVideoErrorAction {
    type: 'DISPLAY_VIDEO_ERROR';
    payload: Error;
    error: boolean;
    meta: {
        cellIndex: CellIndex;
    };
}
export const displayVideoError = actionFactory.errorMeta<DisplayVideoErrorAction, { cellIndex: CellIndex }>('DISPLAY_VIDEO_ERROR');


//
//  DISMISS_VIDEO_ERROR action tells the app to hide the error for the given video cell
//
export interface DismissVideoErrorAction {
    type: 'DISMISS_VIDEO_ERROR';
    meta: {
        cellIndex: CellIndex;
    };
}
export const dismissVideoError = actionFactory.meta<DismissVideoErrorAction, { cellIndex: CellIndex }>('DISMISS_VIDEO_ERROR');



// WEBPACK FOOTER //
// ./src/actions/monitoringActions.ts